import React, { useState, useEffect, useRef } from 'react';
import { Timer, Play, Pause, RotateCcw, Volume2, Plus, Minus } from 'lucide-react';
import { playAlarmSound, stopAllAudio, unlockAudio } from '../utils/audioUtils';
import { MELODIES } from '../constants/melodies';
import TimerVideoModal from './TimerVideoModal';
import './TimerComponent.css';

export default function TimerComponent({
  hours,
  minutes,
  seconds,
  volume,
  growing,
  selectedMelody,
  isRunning,
  timeLeft,
  isPaused,
  isRinging,
  useVideoAlarm,
  youtubeUrl,
  videoId,
  videoTitle,
  audioUnlocked,
  handleUnlockAudio,
  // YouTube player related props
  ytApiReady,
  showPlayPrompt,
  createYouTubePlayer,
  manualStartPlay,
  stopVideo,
  setHours,
  setMinutes,
  setSeconds,
  setVolume,
  setGrowing,
  setSelectedMelody,
  setUseVideoAlarm,
  setYoutubeUrl,
  setVideoId,
  setVideoTitle,
  startTimer,
  pauseTimer,
  resetTimer,
  stopTimer,
  testSound,
  extractVideoId,
  handleUrlChange
}) {
  const audioControlRef = useRef(null);
  
  // Listen to isRinging state changes to play alarm sound
  // Only play melody sound if NOT using video alarm
  useEffect(() => {
    const playTimerAlarm = async () => {
      if (isRinging) {
        // If using video alarm, don't play melody sound (only video will play)
        if (useVideoAlarm && videoId) {
          console.log('Timer is ringing with video alarm - skipping melody sound');
          // Stop any existing audio
          if (audioControlRef.current) {
            audioControlRef.current.stop();
            audioControlRef.current = null;
          }
          return;
        }
        
        console.log('Timer is ringing, playing alarm sound');
        try {
          // Unlock audio first
          await unlockAudio();
          
          // Use new audio system to play timer alarm sound
          const audioControl = await playAlarmSound(selectedMelody, volume / 100, growing);
          audioControlRef.current = audioControl;
        } catch (error) {
          console.error('Timer alarm sound playback failed:', error);
        }
      } else {
        // Stop audio
        if (audioControlRef.current) {
          audioControlRef.current.stop();
          audioControlRef.current = null;
        }
      }
    };

    playTimerAlarm();
  }, [isRinging, selectedMelody, volume, growing, useVideoAlarm, videoId]);

  // Quick timer presets (in seconds)
  const presets = [
    { label: '1 min', seconds: 60 },
    { label: '3 min', seconds: 180 },
    { label: '5 min', seconds: 300 },
    { label: '10 min', seconds: 600 },
    { label: '15 min', seconds: 900 },
    { label: '20 min', seconds: 1200 },
    { label: '25 min', seconds: 1500 }, // Pomodoro
    { label: '30 min', seconds: 1800 },
    { label: '45 min', seconds: 2700 },
    { label: '1 hour', seconds: 3600 }
  ];

  const handleResumeTimer = () => {
    // Use the passed pauseTimer function to resume the timer
    // In the useSingleTimer hook, pauseTimer will handle the resume logic
    pauseTimer();
  };

  const handleStopAlarm = () => {
    // Stop currently playing audio
    if (audioControlRef.current) {
      audioControlRef.current.stop();
      audioControlRef.current = null;
    }
    
    // Ensure all audio is stopped
    stopAllAudio();
    
    // Stop video playback (if using video alarm)
    if (useVideoAlarm && stopVideo) {
      stopVideo();
    }
    
    // Use the passed stopTimer function
    stopTimer();
  };

  // Video modal state management
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Show video modal when timer rings and video alarm is used
  useEffect(() => {
    if (isRinging && useVideoAlarm && videoId) {
      setShowVideoModal(true);
    } else {
      setShowVideoModal(false);
    }
  }, [isRinging, useVideoAlarm, videoId]);

  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
  };

  const handleStopVideoModal = () => {
    setShowVideoModal(false);
    handleStopAlarm();
  };

  const setPreset = (presetSeconds) => {
    if (!isRunning) {
      const h = Math.floor(presetSeconds / 3600);
      const m = Math.floor((presetSeconds % 3600) / 60);
      const s = presetSeconds % 60;
      setHours(h);
      setMinutes(m);
      setSeconds(s);
    }
  };

  const incrementValue = (setter, value, max) => {
    if (!isRunning && !isPaused) {
      setter(prev => (prev + value > max ? max : prev + value));
    }
  };

  const decrementValue = (setter, value) => {
    if (!isRunning && !isPaused) {
      setter(prev => (prev - value < 0 ? 0 : prev - value));
    }
  };

  const formatTimeDisplay = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return {
      hours: h.toString().padStart(2, '0'),
      minutes: m.toString().padStart(2, '0'),
      seconds: s.toString().padStart(2, '0')
    };
  };

  const displayTime = isRunning || isPaused 
    ? formatTimeDisplay(timeLeft)
    : {
        hours: hours.toString().padStart(2, '0'),
        minutes: minutes.toString().padStart(2, '0'),
        seconds: seconds.toString().padStart(2, '0')
      };

  // Calculate progress percentage
  const totalSetTime = hours * 3600 + minutes * 60 + seconds;
  const progress = totalSetTime > 0 ? ((totalSetTime - timeLeft) / totalSetTime) * 100 : 0;

  return (
    <div className="timer-component-container">
      <div className="timer-card">
        <div className="timer-header">
          <Timer className="timer-icon" />
          <h3 className="timer-title">Countdown Timer</h3>
        </div>

        <p className="timer-description">
          Set a countdown timer for cooking, studying, exercise, or any activity.
        </p>

        {/* Timer Display */}
        <div className="timer-display-section">
          <div className="time-display">
            <TimeDigit value={displayTime.hours} label="Hours" />
            <div className="time-separator">:</div>
            <TimeDigit value={displayTime.minutes} label="Minutes" />
            <div className="time-separator">:</div>
            <TimeDigit value={displayTime.seconds} label="Seconds" />
          </div>

          {/* Progress Bar */}
          {(isRunning || isPaused) && (
            <div className="progress-container">
              <div 
                className="progress-bar"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          )}
        </div>

        {/* Time Input Controls */}
        {!isRunning && !isPaused && (
          <div className="time-inputs-grid">
            <TimeInput
              label="Hours"
              value={hours}
              onIncrement={() => incrementValue(setHours, 1, 23)}
              onDecrement={() => decrementValue(setHours, 1)}
              max={23}
            />
            <TimeInput
              label="Minutes"
              value={minutes}
              onIncrement={() => incrementValue(setMinutes, 1, 59)}
              onDecrement={() => decrementValue(setMinutes, 1)}
              max={59}
            />
            <TimeInput
              label="Seconds"
              value={seconds}
              onIncrement={() => incrementValue(setSeconds, 1, 59)}
              onDecrement={() => decrementValue(setSeconds, 1)}
              max={59}
            />
          </div>
        )}

        {/* Quick Presets */}
        {!isRunning && !isPaused && (
          <div className="presets-section">
            <label className="presets-label">
              Quick Presets
            </label>
            <div className="presets-grid">
              {presets.map(preset => (
                <button
                  key={preset.label}
                  onClick={() => setPreset(preset.seconds)}
                  className="preset-button"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Video Alarm Settings */}
        {!isRunning && !isPaused && (
          <div className="video-alarm-settings">
            <div className="video-alarm-toggle">
              <div className="toggle-container">
                <span className="toggle-label">Use Video Alarm?</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={useVideoAlarm}
                    onChange={(e) => setUseVideoAlarm(e.target.checked)}
                    className="toggle-input"
                  />
                  <div className={`toggle-slider ${useVideoAlarm ? 'checked' : ''}`}>
                    <div className={`toggle-thumb ${useVideoAlarm ? 'checked' : ''}`}></div>
                  </div>
                </label>
              </div>
            </div>
            
            {useVideoAlarm && (
              <div className="video-settings">
                <div className="video-input-group">
                  <label className="video-label">YouTube Video URL:</label>
                  <input
                    type="text"
                    value={youtubeUrl}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    placeholder="Enter YouTube video URL (e.g., https://www.youtube.com/watch?v=dQw4w9WgXcQ)"
                    className="video-input"
                  />
                  {videoId && (
                    <p className="text-green-300 text-sm mt-2">✅ Valid video ID: {videoId}</p>
                  )}
                  {youtubeUrl && !videoId && (
                    <p className="text-red-300 text-sm mt-2">❌ Invalid YouTube URL</p>
                  )}
                </div>
                <div className="video-input-group">
                  <label className="video-label">Video Title (optional):</label>
                  <input
                    type="text"
                    value={videoTitle}
                    onChange={(e) => setVideoTitle(e.target.value)}
                    placeholder="Enter video title"
                    className="video-input"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sound Settings */}
        {!isRunning && !isPaused && (
          <div className="sound-settings">
            {/* Melody Selection */}
            <div className="melody-selection">
              <label className="melody-label">
                Choose melody
              </label>
              <div className="melody-controls">
                <select
                  value={selectedMelody}
                  onChange={(e) => setSelectedMelody(e.target.value)}
                  className="melody-select"
                >
                  {MELODIES.map(melody => (
                    <option key={melody.id} value={melody.id}>{melody.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="volume-control">
              <label className="volume-label">
                Volume: {volume}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                className="volume-slider"
              />
            </div>
            <div className="growing-volume-control">
              <div className="toggle-container">
                <span className="toggle-label">Growing Volume?</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={growing}
                    onChange={(e) => setGrowing(e.target.checked)}
                    className="toggle-input"
                  />
                  <div className={`toggle-slider ${growing ? 'checked' : ''}`}>
                    <div className={`toggle-thumb ${growing ? 'checked' : ''}`}></div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Control Buttons */}
        {!isRinging ? (
          <div className="control-buttons">
            {!isRunning && !isPaused && (
              <button
                onClick={startTimer}
                disabled={hours === 0 && minutes === 0 && seconds === 0}
                className="control-btn start-btn"
              >
                <Play className="btn-icon" />
                Start Timer
              </button>
            )}
            
            {isRunning && (
              <button
                onClick={pauseTimer}
                className="control-btn pause-btn"
              >
                <Pause className="btn-icon" />
                Pause
              </button>
            )}
            
            {isPaused && (
              <button
                onClick={handleResumeTimer}
                className="control-btn resume-btn"
              >
                <Play className="btn-icon" />
                Resume
              </button>
            )}
            
            {(isRunning || isPaused) && (
              <button
                onClick={resetTimer}
                className="control-btn reset-btn"
              >
                <RotateCcw className="btn-icon" />
                Reset
              </button>
            )}
          </div>
        ) : (
          // Alarm ringing interface
          <div className="alarm-notification">
            {useVideoAlarm && videoId ? (
              // Video alarm interface - now uses popup modal
              <div className="video-alarm-content">
                <div className="video-alarm-header">
                  <Volume2 className="alarm-icon" />
                  <h3 className="alarm-title">TIME'S UP!</h3>
                  <p className="alarm-message">Timer finished - Video alarm is playing in popup window</p>
                </div>
                
                {/* Video information display */}
                <div className="video-info-display">
                  {videoTitle && (
                    <p className="video-title">🎬 {videoTitle}</p>
                  )}
                  {youtubeUrl && (
                    <p className="video-url">🔗 {youtubeUrl}</p>
                  )}
                  <p className="video-modal-hint">
                    📺 Video is playing in a popup window. If you don't see it, check if popups are blocked.
                  </p>
                </div>
                
                {/* Stop button */}
                <button
                  onClick={handleStopAlarm}
                  className="stop-alarm-btn video-stop-btn"
                >
                  Stop Timer & Video
                </button>
              </div>
            ) : (
              // Regular audio alarm interface
              <div className="alarm-content">
                <Volume2 className="alarm-icon" />
                <h3 className="alarm-title">TIME'S UP!</h3>
                <p className="alarm-message">Your timer has finished</p>
                <button
                  onClick={handleStopAlarm}
                  className="stop-alarm-btn"
                >
                  Stop Timer
                </button>
              </div>
            )}
          </div>
        )}

        {/* Status Message */}
        {isRunning && (
          <div className="status-message running">
            <p className="status-text">
              ⏱️ Timer is running... {displayTime.hours}:{displayTime.minutes}:{displayTime.seconds} remaining
            </p>
          </div>
        )}

        {isPaused && (
          <div className="status-message paused">
            <p className="status-text">
              ⏸️ Timer paused at {displayTime.hours}:{displayTime.minutes}:{displayTime.seconds}
            </p>
          </div>
        )}
      </div>


      {/* Timer Video Modal */}
      <TimerVideoModal
        isOpen={showVideoModal}
        onClose={handleCloseVideoModal}
        onStop={handleStopVideoModal}
        videoId={videoId}
        videoTitle={videoTitle}
        youtubeUrl={youtubeUrl}
        volume={volume}
        onVolumeChange={setVolume}
        ytApiReady={ytApiReady}
        showPlayPrompt={showPlayPrompt}
        createYouTubePlayer={createYouTubePlayer}
        manualStartPlay={manualStartPlay}
        stopVideo={stopVideo}
      />
    </div>
  );
}

// Time Digit Display Component
function TimeDigit({ value, label }) {
  return (
    <div className="time-digit-container">
      <div className="time-digit-display">
        <div className="time-digit-value">
          {value}
        </div>
      </div>
      <span className="time-digit-label">{label}</span>
    </div>
  );
}

// Time Input Component with +/- buttons
function TimeInput({ label, value, onIncrement, onDecrement, max }) {
  return (
    <div className="time-input-container">
      <label className="time-input-label">
        {label}
      </label>
      <div className="time-input-controls">
        <button
          onClick={onIncrement}
          className="time-input-btn increment-btn"
        >
          <Plus className="time-input-icon" />
        </button>
        <div className="time-input-display">
          <span className="time-input-value">
            {value.toString().padStart(2, '0')}
          </span>
        </div>
        <button
          onClick={onDecrement}
          className="time-input-btn decrement-btn"
        >
          <Minus className="time-input-icon" />
        </button>
      </div>
    </div>
  );
}